import { useState, useEffect } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { API } from "../App";
import Layout from "../components/Layout";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  FileText,
  Sparkles,
  Loader2,
  Send,
  Eye,
  Edit3,
  Trash2,
  ExternalLink,
  Upload,
  CheckCircle,
  Clock,
  BookOpen,
  RefreshCw,
  Copy,
  Save,
  ChevronDown,
  ChevronUp,
  Info,
  Zap
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";

const statusConfig = {
  draft: { color: "bg-yellow-100 text-yellow-700", label: "Draft", icon: Edit3 },
  pushed_to_hubspot: { color: "bg-blue-100 text-blue-700", label: "In HubSpot", icon: Upload },
  published: { color: "bg-emerald-100 text-emerald-700", label: "Published", icon: CheckCircle }
};

const DraftCard = ({ draft, onView, onDelete, onPushToHubspot }) => {
  const status = statusConfig[draft.status] || statusConfig.draft;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="border border-slate-200 rounded-xl bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-slate-900 truncate">{draft.title}</h3>
          <p className="text-sm text-slate-500 mt-1 line-clamp-2">{draft.meta_description}</p>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Badge className={`${status.color} text-xs gap-1`}>
              <StatusIcon className="w-3 h-3" />
              {status.label}
            </Badge>
            <span className="text-xs text-slate-400">
              {new Date(draft.created_at).toLocaleDateString()}
            </span>
            {draft.style_sources?.length > 0 && (
              <Badge variant="outline" className="text-xs border-slate-200">
                Style: {draft.style_sources.length} articles
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Button variant="ghost" size="sm" onClick={() => onView(draft)} className="h-8 px-2">
            <Eye className="w-4 h-4" />
          </Button>
          {draft.status === "draft" && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onPushToHubspot(draft.draft_id)}
              className="h-8 px-2 text-blue-600 hover:text-blue-700"
            >
              <Upload className="w-4 h-4" />
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onDelete(draft.draft_id)}
            className="h-8 px-2 text-red-500 hover:text-red-600"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

const ArticlePreviewDialog = ({ draft, open, onOpenChange, onSave, onPushToHubspot }) => {
  const [editMode, setEditMode] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [editedContent, setEditedContent] = useState("");
  const [editedMeta, setEditedMeta] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (draft) {
      setEditedTitle(draft.title || "");
      setEditedContent(draft.content || "");
      setEditedMeta(draft.meta_description || "");
    }
  }, [draft]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(draft.draft_id, {
        title: editedTitle,
        content: editedContent,
        meta_description: editedMeta
      });
      setEditMode(false);
      toast.success("Draft saved successfully");
    } catch (error) {
      toast.error("Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  if (!draft) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl">
                {editMode ? "Edit Article" : "Article Preview"}
              </DialogTitle>
              <DialogDescription>
                {draft.status === "draft" ? "Review and edit before pushing to HubSpot" : "View your KB article"}
              </DialogDescription>
            </div>
            {draft.status === "draft" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditMode(!editMode)}
                className="gap-2"
              >
                {editMode ? <Eye className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                {editMode ? "Preview" : "Edit"}
              </Button>
            )}
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          {editMode ? (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Title</label>
                <Input
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Meta Description</label>
                <Textarea
                  value={editedMeta}
                  onChange={(e) => setEditedMeta(e.target.value)}
                  className="mt-1"
                  rows={2}
                />
                <p className="text-xs text-slate-400 mt-1">{editedMeta.length}/160 characters</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Content (HTML)</label>
                <Textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  className="mt-1 font-mono text-sm"
                  rows={15}
                />
              </div>
            </div>
          ) : (
            <div className="py-4 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-slate-900">{draft.title}</h2>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(draft.title)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-slate-600">{draft.meta_description}</p>
              </div>

              {draft.style_sources?.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <BookOpen className="w-4 h-4" />
                  <span>Style based on: {draft.style_sources.join(", ")}</span>
                </div>
              )}

              <div className="border border-slate-200 rounded-lg p-6 bg-white">
                <div 
                  className="prose prose-slate max-w-none"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(draft.content) }}
                />
              </div>
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="mt-4 pt-4 border-t">
          {editMode ? (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="gap-2 bg-[#425B76] hover:bg-[#2D4156]">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Changes
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
              {draft.status === "draft" && (
                <Button 
                  onClick={() => onPushToHubspot(draft.draft_id)}
                  className="gap-2 bg-[#F5C518] hover:bg-[#E5B516] text-slate-900"
                >
                  <Upload className="w-4 h-4" />
                  Push to HubSpot
                </Button>
              )}
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const KBGeneratorPage = () => {
  const [activeTab, setActiveTab] = useState("generate");
  const [featureContent, setFeatureContent] = useState("");
  const [featureTitle, setFeatureTitle] = useState("");
  const [targetAudience, setTargetAudience] = useState("all");
  const [generating, setGenerating] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(true);
  const [stylePreview, setStylePreview] = useState(null);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showTips, setShowTips] = useState(true);

  useEffect(() => {
    fetchDrafts();
    fetchStylePreview();
  }, []);

  const fetchDrafts = async () => {
    try {
      const response = await axios.get(`${API}/kb-generator/drafts`, {
        withCredentials: true
      });
      setDrafts(response.data.drafts || []);
    } catch (error) {
      toast.error("Failed to load saved drafts");
    } finally {
      setLoadingDrafts(false);
    }
  };

  const fetchStylePreview = async () => {
    try {
      const response = await axios.get(`${API}/kb-generator/style-preview`, {
        withCredentials: true
      });
      setStylePreview(response.data);
    } catch (error) {
      setStylePreview(null);
    }
  };

  const generateArticle = async () => {
    if (!featureContent.trim()) {
      toast.error("Please enter feature content");
      return;
    }

    if (featureContent.trim().length < 50) {
      toast.error("Please provide more details about the feature (at least 50 characters)");
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/kb-generator/generate`, {
        feature_content: featureContent,
        feature_title: featureTitle || null,
        target_audience: targetAudience === "all" ? null : targetAudience
      }, {
        withCredentials: true
      });

      toast.success("Article generated successfully!");
      setSelectedDraft(response.data);
      setPreviewOpen(true);
      setFeatureContent("");
      setFeatureTitle("");
      fetchDrafts();
    } catch (error) {
      // Error handled silently
      toast.error(error.response?.data?.detail || "Failed to generate article");
    } finally {
      setGenerating(false);
    }
  };

  const handleViewDraft = (draft) => {
    setSelectedDraft(draft);
    setPreviewOpen(true);
  };

  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm("Are you sure you want to delete this draft?")) return;

    try {
      await axios.delete(`${API}/kb-generator/drafts/${draftId}`, {
        withCredentials: true
      });
      toast.success("Draft deleted");
      fetchDrafts();
    } catch (error) {
      toast.error("Failed to delete draft");
    }
  };

  const handleSaveDraft = async (draftId, updates) => {
    await axios.put(`${API}/kb-generator/drafts/${draftId}`, updates, {
      withCredentials: true
    });
    fetchDrafts();
    // Update selected draft
    setSelectedDraft(prev => ({ ...prev, ...updates }));
  };

  const handlePushToHubspot = async (draftId) => {
    if (!window.confirm("Push this article to HubSpot as a draft? You can review and publish it there.")) return;

    try {
      const response = await axios.post(`${API}/kb-generator/drafts/${draftId}/push-to-hubspot`, {}, {
        withCredentials: true
      });
      toast.success(response.data.message);
      setPreviewOpen(false);
      fetchDrafts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to push to HubSpot");
    }
  };

  return (
    <Layout title="KB Article Generator">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">KB Article Generator</h1>
              <p className="text-sm text-slate-500">Generate knowledge base articles from feature specs</p>
            </div>
          </div>
          {stylePreview && (
            <Badge variant="outline" className="text-sm border-emerald-200 text-emerald-600 bg-emerald-50 gap-1">
              <BookOpen className="w-4 h-4" />
              {stylePreview.total_articles_available} KB articles for style reference
            </Badge>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="generate" className="gap-2">
              <Zap className="w-4 h-4" />
              Generate Article
            </TabsTrigger>
            <TabsTrigger value="drafts" className="gap-2">
              <FileText className="w-4 h-4" />
              My Drafts ({drafts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="generate" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Generator Form */}
              <div className="lg:col-span-2">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5 text-violet-500" />
                      Feature Details
                    </CardTitle>
                    <CardDescription>
                      Paste your feature specification, release notes, or Notion content
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Feature Title (Optional)</label>
                      <Input
                        value={featureTitle}
                        onChange={(e) => setFeatureTitle(e.target.value)}
                        placeholder="e.g., Bulk Invoice Export Feature"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-700">Target Audience</label>
                      <Select value={targetAudience} onValueChange={setTargetAudience}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select audience" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Users</SelectItem>
                          <SelectItem value="managers">Managers Only</SelectItem>
                          <SelectItem value="providers">Providers Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-700">
                        Feature Content <span className="text-red-500">*</span>
                      </label>
                      <Textarea
                        value={featureContent}
                        onChange={(e) => setFeatureContent(e.target.value)}
                        placeholder={`Paste your feature details here. Include:

• Feature name and purpose
• What problem it solves
• Who it's for (user types)
• Step-by-step usage instructions
• Screenshots or image descriptions
• Edge cases or limitations
• FAQs if any

The more detail you provide, the better the generated article will be.`}
                        className="mt-1 min-h-[300px] font-mono text-sm"
                        data-testid="feature-content-input"
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        {featureContent.length} characters (minimum 50 required)
                      </p>
                    </div>

                    <Button
                      onClick={generateArticle}
                      disabled={generating || featureContent.trim().length < 50}
                      className="w-full gap-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                      data-testid="generate-article-btn"
                    >
                      {generating ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Generating Article...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          Generate KB Article
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Tips Sidebar */}
              <div className="space-y-4">
                <Card className="border-slate-200 bg-gradient-to-br from-violet-50 to-purple-50">
                  <CardHeader className="pb-2">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setShowTips(!showTips)}
                    >
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Info className="w-4 h-4 text-violet-600" />
                        How It Works
                      </CardTitle>
                      {showTips ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </CardHeader>
                  <AnimatePresence>
                    {showTips && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                      >
                        <CardContent className="pt-0">
                          <ol className="text-sm text-slate-600 space-y-3">
                            <li className="flex gap-2">
                              <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center flex-shrink-0 text-xs font-medium">1</span>
                              <span>Paste your feature details from Notion or anywhere</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center flex-shrink-0 text-xs font-medium">2</span>
                              <span>AI analyzes your existing HubSpot KB articles</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center flex-shrink-0 text-xs font-medium">3</span>
                              <span>Generates a new article matching your writing style</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center flex-shrink-0 text-xs font-medium">4</span>
                              <span>Review, edit, and push to HubSpot as a draft</span>
                            </li>
                          </ol>
                        </CardContent>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>

                {stylePreview && stylePreview.sample_articles?.length > 0 && (
                  <Card className="border-slate-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-emerald-600" />
                        Style Reference Articles
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-2">
                        {stylePreview.sample_articles.slice(0, 3).map((article) => (
                          <a
                            key={article.url || article.title}
                            href={article.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block p-2 rounded-lg hover:bg-slate-50 transition-colors group"
                          >
                            <p className="text-sm font-medium text-slate-700 group-hover:text-violet-600 flex items-center gap-1">
                              {article.title.slice(0, 40)}...
                              <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                            </p>
                          </a>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="drafts" className="mt-6">
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Article Drafts</CardTitle>
                  <Button variant="outline" size="sm" onClick={fetchDrafts} className="gap-2">
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingDrafts ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : drafts.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No drafts yet</p>
                    <p className="text-sm text-slate-400 mt-1">Generate your first KB article to get started</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => setActiveTab("generate")}
                    >
                      Generate Article
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {drafts.map((draft) => (
                      <DraftCard
                        key={draft.draft_id}
                        draft={draft}
                        onView={handleViewDraft}
                        onDelete={handleDeleteDraft}
                        onPushToHubspot={handlePushToHubspot}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Preview Dialog */}
        <ArticlePreviewDialog
          draft={selectedDraft}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          onSave={handleSaveDraft}
          onPushToHubspot={handlePushToHubspot}
        />
      </div>
    </Layout>
  );
};

export default KBGeneratorPage;
